import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Calculator,
  Loader2,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  Search,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { cashSettlementApi, CashSettlement, CashSettlementStatus } from '@/services/cashSettlementApi';
import { branchApi } from '@/services/branchApi';
import SettlementDetailModal from '@/components/cash-settlement/SettlementDetailModal';
import SecurityPinModal from '@/components/common/SecurityPinModal';
import { getCurrencySymbol } from '@/utils/currency';
import { format } from 'date-fns';
import SimpleSelect from '@/components/common/SimpleSelect';
import DatePicker from '@/components/common/DatePicker';

type TabKey = 'pending' | 'all' | 'verified' | 'rejected';

const TABS: { key: TabKey; label: string; status?: CashSettlementStatus }[] = [
  { key: 'pending', label: 'Pending Approval' },
  { key: 'all', label: 'All' },
  { key: 'verified', label: 'Verified', status: 'VERIFIED' },
  { key: 'rejected', label: 'Rejected', status: 'REJECTED' },
];

const STATUS_CONFIG: Record<CashSettlementStatus, { label: string; color: string; bg: string; icon: typeof Clock }> = {
  PENDING: { label: 'Pending', color: 'text-yellow-700', bg: 'bg-yellow-100', icon: Clock },
  SUBMITTED: { label: 'Submitted', color: 'text-blue-700', bg: 'bg-blue-100', icon: Send },
  VERIFIED: { label: 'Verified', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-100', icon: XCircle },
};

export default function CashSettlementPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = (searchParams.get('tab') as TabKey) || 'pending';
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Modal states
  const [detailSettlement, setDetailSettlement] = useState<CashSettlement | null>(null);
  const [actionSettlement, setActionSettlement] = useState<CashSettlement | null>(null);
  const [actionType, setActionType] = useState<'verify' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  // Fetch branches for filter
  const { data: branches = [] } = useQuery({
    queryKey: ['branches-list-simple'],
    queryFn: () => branchApi.getBranchList(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch pending count for tab badge
  const { data: pendingCount } = useQuery({
    queryKey: ['settlement-pending-count'],
    queryFn: () => cashSettlementApi.getPendingCount(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });

  // Fetch settlements based on active tab
  const statusFilter = activeTab === 'pending' ? 'SUBMITTED' as CashSettlementStatus :
    activeTab === 'verified' ? 'VERIFIED' as CashSettlementStatus :
    activeTab === 'rejected' ? 'REJECTED' as CashSettlementStatus :
    undefined;

  const { data: settlementsData, isLoading } = useQuery({
    queryKey: ['settlements', activeTab, selectedBranchId, startDate, endDate, page],
    queryFn: () => cashSettlementApi.getAll({
      branchId: selectedBranchId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      status: statusFilter,
      page,
      limit,
    }),
    staleTime: 30 * 1000,
  });

  const settlements = settlementsData?.settlements || [];
  const pagination = settlementsData?.pagination;

  // Verify mutation
  const verifyMutation = useMutation({
    mutationFn: ({ id, notes, securityPin }: { id: string; notes?: string; securityPin: string }) =>
      cashSettlementApi.verify(id, { notes, securityPin }),
    onSuccess: () => {
      toast.success('Settlement verified successfully');
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['settlement-pending-count'] });
      resetActionState();
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || 'Failed to verify settlement';
      setPinError(msg);
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: ({ id, reason, securityPin }: { id: string; reason: string; securityPin: string }) =>
      cashSettlementApi.reject(id, { reason, securityPin }),
    onSuccess: () => {
      toast.success('Settlement rejected');
      queryClient.invalidateQueries({ queryKey: ['settlements'] });
      queryClient.invalidateQueries({ queryKey: ['settlement-pending-count'] });
      resetActionState();
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || 'Failed to reject settlement';
      setPinError(msg);
    },
  });

  const resetActionState = () => {
    setActionSettlement(null);
    setActionType(null);
    setShowPinModal(false);
    setShowRejectInput(false);
    setRejectReason('');
    setVerifyNotes('');
    setPinError(null);
    setDetailSettlement(null);
  };

  const handleVerifyClick = (settlement: CashSettlement) => {
    setActionSettlement(settlement);
    setActionType('verify');
    setVerifyNotes('');
    setShowPinModal(true);
    setPinError(null);
  };

  const handleRejectClick = (settlement: CashSettlement) => {
    setActionSettlement(settlement);
    setActionType('reject');
    setRejectReason('');
    setShowRejectInput(true);
    setPinError(null);
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setShowRejectInput(false);
    setShowPinModal(true);
  };

  const handlePinVerify = async (pin: string) => {
    if (!actionSettlement || !actionType) return;

    if (actionType === 'verify') {
      await verifyMutation.mutateAsync({
        id: actionSettlement.id,
        notes: verifyNotes || undefined,
        securityPin: pin,
      });
    } else {
      await rejectMutation.mutateAsync({
        id: actionSettlement.id,
        reason: rejectReason,
        securityPin: pin,
      });
    }
  };

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setPage(1);
    setSearchParams({ tab });
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM dd, yyyy');
    } catch {
      return dateStr;
    }
  };

  const submittedCount = pendingCount?.submittedCount || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Calculator className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cash Settlement</h1>
            <p className="text-sm text-gray-600">Manage and approve branch cash settlements</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-3 sm:px-4 py-2 text-sm rounded-lg font-medium transition-colors relative ${
              activeTab === tab.key
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.key === 'pending' && submittedCount > 0 && (
              <span className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                activeTab === 'pending' ? 'bg-white text-purple-600' : 'bg-red-500 text-white'
              }`}>
                {submittedCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3">
          <SimpleSelect
            value={selectedBranchId}
            onChange={(v) => { setSelectedBranchId(v); setPage(1); }}
            options={[
              { value: '', label: 'All Branches' },
              ...branches.map((b) => ({ value: b.id, label: b.name })),
            ]}
            placeholder="All Branches"
            aria-label="Filter by branch"
            wrapperClassName="min-w-[160px]"
            className="focus:!ring-purple-500 focus:!border-purple-500"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">From:</span>
            <DatePicker
              value={startDate}
              onChange={(v) => { setStartDate(v); setPage(1); }}
              wrapperClassName="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">To:</span>
            <DatePicker
              value={endDate}
              onChange={(v) => { setEndDate(v); setPage(1); }}
              wrapperClassName="w-40"
            />
          </div>
          {(selectedBranchId || startDate || endDate) && (
            <button
              onClick={() => { setSelectedBranchId(''); setStartDate(''); setEndDate(''); setPage(1); }}
              className="text-sm text-purple-600 hover:text-purple-800 font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      )}

      {/* Desktop Table */}
      {!isLoading && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Branch</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Date</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Settled By</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Total Received</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Digital (UPI/Card)</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Cash Received</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Expected Cash</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Cash in Hand</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Shortage / Surplus</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3">Status</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {settlements.map((s) => {
                  const sc = STATUS_CONFIG[s.status];
                  const StatusIcon = sc.icon;
                  // Calculate cash-only amounts from method breakdowns
                  const cashCollected = s.methodBreakdowns
                    ?.filter((m) => m.paymentMethod.name.toLowerCase() === 'cash')
                    .reduce((sum, m) => sum + Number(m.collectedAmount), 0) || 0;
                  const digitalTotal = s.methodBreakdowns
                    ?.filter((m) => {
                      const name = m.paymentMethod.name.toLowerCase();
                      return name !== 'cash';
                    })
                    .reduce((sum, m) => sum + Number(m.collectedAmount), 0) || 0;
                  const expectedCash = cashCollected - Number(s.totalRefunds || 0) - Number(s.totalOutSpare || 0);
                  const physical = Number(s.physicalCashCount || 0);
                  const diff = physical - expectedCash;
                  return (
                    <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                            <span className="text-purple-600 font-bold text-xs">{s.branch?.code || '?'}</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{s.branch?.name || '-'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900">{formatDate(s.settlementDate)}</td>
                      <td className="px-3 py-3 text-sm text-gray-600">{s.settledBy?.name || '-'}</td>
                      <td className="px-3 py-3 text-sm text-right text-green-600 font-medium">{getCurrencySymbol()}{Number(s.totalCollected).toLocaleString()}</td>
                      <td className="px-3 py-3 text-sm text-right text-blue-600 font-medium">{digitalTotal > 0 ? `${getCurrencySymbol()}${digitalTotal.toLocaleString()}` : <span className="text-gray-400">{getCurrencySymbol()}0</span>}</td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-gray-900">{getCurrencySymbol()}{cashCollected.toLocaleString()}</td>
                      <td className="px-3 py-3 text-sm text-right font-bold text-gray-900">
                        <div>{getCurrencySymbol()}{expectedCash.toLocaleString()}</div>
                        {(Number(s.totalRefunds || 0) > 0 || Number(s.totalOutSpare || 0) > 0) && (
                          <div className="text-[10px] text-gray-400 font-normal">
                            {Number(s.totalRefunds || 0) > 0 && <span>-{getCurrencySymbol()}{Number(s.totalRefunds).toLocaleString()} refund</span>}
                            {Number(s.totalRefunds || 0) > 0 && Number(s.totalOutSpare || 0) > 0 && ', '}
                            {Number(s.totalOutSpare || 0) > 0 && <span>-{getCurrencySymbol()}{Number(s.totalOutSpare).toLocaleString()} spare</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-right font-medium text-gray-900">{getCurrencySymbol()}{physical.toLocaleString()}</td>
                      <td className="px-3 py-3 text-sm text-right">
                        {diff === 0 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">Matched</span>
                        ) : diff > 0 ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">Surplus +{getCurrencySymbol()}{Math.abs(diff).toLocaleString()}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700">Short -{getCurrencySymbol()}{Math.abs(diff).toLocaleString()}</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${sc.bg} ${sc.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailSettlement(s)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {s.status === 'SUBMITTED' && (
                            <>
                              <button
                                onClick={() => handleVerifyClick(s)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Verify"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRejectClick(s)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {settlements.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center py-12 text-gray-500 text-sm">
                      No settlements found for the selected filters
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-100">
            {settlements.map((s) => {
              const sc = STATUS_CONFIG[s.status];
              const StatusIcon = sc.icon;
              // Calculate cash-only amounts from method breakdowns
              const cashCollected = s.methodBreakdowns
                ?.filter((m) => m.paymentMethod.name.toLowerCase() === 'cash')
                .reduce((sum, m) => sum + Number(m.collectedAmount), 0) || 0;
              const digitalTotal = s.methodBreakdowns
                ?.filter((m) => m.paymentMethod.name.toLowerCase() !== 'cash')
                .reduce((sum, m) => sum + Number(m.collectedAmount), 0) || 0;
              const expectedCash = cashCollected - Number(s.totalRefunds || 0) - Number(s.totalOutSpare || 0);
              const physical = Number(s.physicalCashCount || 0);
              const diff = physical - expectedCash;
              return (
                <div key={s.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-bold text-xs">{s.branch?.code || '?'}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{s.branch?.name || '-'}</p>
                        <p className="text-xs text-gray-500">{formatDate(s.settlementDate)}</p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${sc.bg} ${sc.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {sc.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Total Received</p>
                      <p className="text-sm font-medium text-green-600">{getCurrencySymbol()}{Number(s.totalCollected).toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Digital</p>
                      <p className="text-sm font-medium text-blue-600">{digitalTotal > 0 ? `${getCurrencySymbol()}${digitalTotal.toLocaleString()}` : `${getCurrencySymbol()}0`}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Cash Received</p>
                      <p className="text-sm font-medium text-gray-900">{getCurrencySymbol()}{cashCollected.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Expected Cash</p>
                      <p className="text-sm font-bold text-gray-900">{getCurrencySymbol()}{expectedCash.toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-gray-500 uppercase">Cash in Hand</p>
                      <p className="text-sm font-medium text-gray-900">{getCurrencySymbol()}{physical.toLocaleString()}</p>
                    </div>
                    <div className={`rounded-lg p-2 text-center ${diff === 0 ? 'bg-green-50' : diff > 0 ? 'bg-blue-50' : 'bg-red-50'}`}>
                      <p className="text-[10px] text-gray-500 uppercase">{diff === 0 ? 'Status' : diff > 0 ? 'Surplus' : 'Shortage'}</p>
                      <p className={`text-sm font-medium ${diff === 0 ? 'text-green-700' : diff > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                        {diff === 0 ? 'Matched' : `${getCurrencySymbol()}${Math.abs(diff).toLocaleString()}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">By: {s.settledBy?.name || '-'}</p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setDetailSettlement(s)}
                        className="px-2.5 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                      >
                        View
                      </button>
                      {s.status === 'SUBMITTED' && (
                        <>
                          <button
                            onClick={() => handleVerifyClick(s)}
                            className="px-2.5 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => handleRejectClick(s)}
                            className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {settlements.length === 0 && (
              <div className="text-center py-12 text-gray-500 text-sm">
                No settlements found
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {(pagination.page - 1) * pagination.limit + 1}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 text-sm rounded-lg font-medium transition-colors ${
                        page === pageNum
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= (pagination?.totalPages || 1)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <SettlementDetailModal
        isOpen={!!detailSettlement}
        onClose={() => setDetailSettlement(null)}
        settlement={detailSettlement}
        onVerify={handleVerifyClick}
        onReject={handleRejectClick}
      />

      {/* Reject Reason Input Modal */}
      {showRejectInput && actionSettlement && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) { setShowRejectInput(false); resetActionState(); } }}>
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Reject Settlement</h3>
              <p className="text-sm text-gray-500 mb-4">
                {actionSettlement.settlementNumber} - {actionSettlement.branch?.name}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                autoFocus
              />
              <div className="flex items-center justify-end gap-3 mt-4">
                <button
                  onClick={() => { setShowRejectInput(false); resetActionState(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectConfirm}
                  disabled={!rejectReason.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security PIN Modal */}
      <SecurityPinModal
        isOpen={showPinModal}
        onClose={() => { setShowPinModal(false); setPinError(null); }}
        onVerify={handlePinVerify}
        title={actionType === 'verify' ? 'Verify Settlement' : 'Reject Settlement'}
        description={`Enter your security PIN to ${actionType === 'verify' ? 'verify' : 'reject'} this settlement`}
        isLoading={verifyMutation.isPending || rejectMutation.isPending}
        error={pinError}
      />
    </div>
  );
}
